/**
 * User geography on UserMaster CSV columns (CountryCode, ZoneCode, RegionCode, TERRITORYCODE).
 * Format: 0000,<code1>,<code2> — no scope-table sentinels on save.
 */
import { applyLocationMappingsToForm } from "./userLocationMapping";

export const USER_TERRITORY_ALL_SENTINEL = "__CSRP_ALL_TERRITORIES__";
export const USER_GEO_SCOPED_SENTINEL = "__CSRP_GEO_SCOPED__";
export const USER_ALL_COUNTRIES_SENTINEL = "__CSRP_ALL_COUNTRIES__";
export const USER_ALL_ZONES_SENTINEL = "__CSRP_ALL_ZONES__";
export const USER_ALL_REGIONS_SENTINEL = "__CSRP_ALL_REGIONS__";
export const USER_DEFAULT_GEO_CODE = "0000";

const ALL_SENTINELS_BY_LEVEL = {
  country: USER_ALL_COUNTRIES_SENTINEL,
  zone: USER_ALL_ZONES_SENTINEL,
  region: USER_ALL_REGIONS_SENTINEL,
  territory: USER_TERRITORY_ALL_SENTINEL,
};

const splitCsv = (v) =>
  typeof v === "string"
    ? v
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : Array.isArray(v)
      ? v.map((x) => String(x || "").trim()).filter(Boolean)
      : [];

const cleanGeoCodes = (codes) =>
  (Array.isArray(codes) ? codes : [])
    .map((c) => String(c ?? "").trim())
    .filter(
      (c) =>
        c &&
        c !== USER_DEFAULT_GEO_CODE &&
        c !== USER_GEO_SCOPED_SENTINEL &&
        !Object.values(ALL_SENTINELS_BY_LEVEL).includes(c)
    );

const mergeGeoCsvWithDefault = (codes) => {
  const list = cleanGeoCodes(codes);
  if (!list.length) return USER_DEFAULT_GEO_CODE;
  return [USER_DEFAULT_GEO_CODE, ...list].join(",");
};

const resolveLevelForSave = (selectedCodes, allOptionIds, allSentinel) => {
  const sel = cleanGeoCodes(selectedCodes);
  const allIds = cleanGeoCodes(allOptionIds);
  const allSelected = allIds.length > 0 && allIds.every((id) => sel.includes(id));

  if (allSelected) {
    return {
      scopeMode: "ALL",
      codes: sel,
      csv: mergeGeoCsvWithDefault(allIds.length ? allIds : sel),
    };
  }
  if (!sel.length) {
    return { scopeMode: "NONE", codes: [], csv: USER_DEFAULT_GEO_CODE };
  }
  if (sel.length === 1) {
    return { scopeMode: "SINGLE", codes: sel, csv: mergeGeoCsvWithDefault(sel) };
  }
  return {
    scopeMode: "MULTIPLE",
    codes: sel,
    csv: mergeGeoCsvWithDefault(sel),
  };
};

/** Build payload fields for create/update user (arrays + scope modes + legacy CSV). */
export function resolveUserGeoCodesForSave(
  formData,
  {
    allCountryIds = [],
    allZoneIds = [],
    allRegionIds = [],
    allTerritoryIds = [],
  } = {}
) {
  const country = resolveLevelForSave(
    formData?.countryCodes,
    allCountryIds,
    USER_ALL_COUNTRIES_SENTINEL
  );
  const zone = resolveLevelForSave(formData?.zoneCodes, allZoneIds, USER_ALL_ZONES_SENTINEL);
  const region = resolveLevelForSave(
    formData?.regionCodes,
    allRegionIds,
    USER_ALL_REGIONS_SENTINEL
  );
  const territory = resolveLevelForSave(
    formData?.territoryCodes,
    allTerritoryIds,
    USER_TERRITORY_ALL_SENTINEL
  );

  return {
    countryCodes: country.codes,
    zoneCodes: zone.codes,
    regionCodes: region.codes,
    territoryCodes: territory.codes,
    countryScopeMode: country.scopeMode,
    zoneScopeMode: zone.scopeMode,
    regionScopeMode: region.scopeMode,
    territoryScopeMode: territory.scopeMode,
    countryCode: country.csv,
    zoneCode: zone.csv,
    regionCode: region.csv,
    territoryCode: territory.csv,
  };
}

/** Read geo codes from API user row (prefers expanded arrays from backend). */
export function pickUserGeoCodesFromApi(user, level) {
  const codesKey = `${level}Codes`;
  const csvKey = `${level}Code`;
  const scopeKey = `${level}ScopeMode`;
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  const scopeMode = user?.[scopeKey] ?? user?.[cap(scopeKey)] ?? "";
  const fromArray = user?.[codesKey] ?? user?.[cap(codesKey)];
  if (Array.isArray(fromArray) && fromArray.length) {
    return { codes: cleanGeoCodes(fromArray), expandAll: false, scopeMode };
  }
  if (Array.isArray(fromArray) && String(scopeMode).toUpperCase() === "MULTIPLE") {
    return { codes: [], expandAll: false, scopeMode: "MULTIPLE" };
  }

  if (String(scopeMode).toUpperCase() === "ALL") {
    return { codes: [], expandAll: true, scopeMode: "ALL" };
  }

  const raw = user?.[csvKey] ?? user?.[cap(csvKey)] ?? "";
  const rawTokens = splitCsv(raw);
  const parts = cleanGeoCodes(rawTokens);

  /** UserMaster / UserLocationMapping: CSV of only 0000 means ALL at this level. */
  if (rawTokens.includes(USER_DEFAULT_GEO_CODE) && parts.length === 0) {
    return { codes: [], expandAll: true, scopeMode: "ALL" };
  }

  if (parts.length) {
    return {
      codes: parts,
      expandAll: false,
      scopeMode: parts.length === 1 ? "SINGLE" : "MULTIPLE",
    };
  }

  const allSentinel = ALL_SENTINELS_BY_LEVEL[level];
  if (splitCsv(raw).includes(allSentinel)) {
    return { codes: [], expandAll: true, scopeMode: "ALL" };
  }
  if (splitCsv(raw).includes(USER_GEO_SCOPED_SENTINEL)) {
    return { codes: [], expandAll: false, scopeMode: "MULTIPLE" };
  }
  return { codes: [], expandAll: false, scopeMode: "NONE" };
}

export function isUserGeoDefaultCode(value) {
  if (value == null) return false;
  return String(value).trim() === USER_DEFAULT_GEO_CODE;
}

export function isUserGeoScopedSentinel(value) {
  if (value == null) return false;
  return String(value).trim() === USER_GEO_SCOPED_SENTINEL;
}

export function isUserTerritoryAllSentinel(value) {
  if (value == null) return false;
  return String(value).trim() === USER_TERRITORY_ALL_SENTINEL;
}

export function isUserGeoAllSentinel(value) {
  if (value == null) return false;
  const v = String(value).trim();
  return Object.values(ALL_SENTINELS_BY_LEVEL).includes(v);
}

export function resolveUserSpecificationType(rowOrSpec) {
  if (rowOrSpec != null && typeof rowOrSpec === "object") {
    const spec = rowOrSpec.userSpecificationType ?? rowOrSpec.UserSpecificationType;
    if (spec != null && String(spec).trim() !== "") {
      const norm = String(spec).trim();
      if (norm.toLowerCase() === "none") return "General";
      return norm;
    }
    const plantIds = rowOrSpec.plantIds ?? rowOrSpec.PlantIds ?? "";
    const rdcIds = rowOrSpec.rdcIds ?? rowOrSpec.RdcIds ?? "";
    if (String(plantIds).trim()) return "Plant";
    if (String(rdcIds).trim()) return "RDC";
    return "";
  }
  const s = String(rowOrSpec ?? "").trim();
  if (s.toLowerCase() === "none") return "General";
  return s;
}

export function isPlantOrRdcUserSpec(rowOrSpec) {
  const s = resolveUserSpecificationType(rowOrSpec);
  return s === "Plant" || s === "RDC";
}

export function isGeneralUserSpec(rowOrSpec) {
  // Plant/RDC hide geo columns; BCM/CPD/General still show Country/Zone/Region/Territory
  if (isPlantOrRdcUserSpec(rowOrSpec)) return false;
  return true;
}

const stripDefaultGeoToken = (csv) => {
  if (!csv) return "";
  return String(csv)
    .split(",")
    .map((x) => x.trim())
    .filter(
      (x) =>
        x &&
        x !== USER_DEFAULT_GEO_CODE &&
        x !== USER_GEO_SCOPED_SENTINEL &&
        !Object.values(ALL_SENTINELS_BY_LEVEL).includes(x)
    )
    .join(", ");
};

export function formatUserGeoColumnDisplay(rowOrSpec, name, code) {
  if (!isGeneralUserSpec(rowOrSpec)) return "-";
  const cleanName = stripDefaultGeoToken(name);
  const cleanCode = stripDefaultGeoToken(code);
  if (cleanName) {
    // Deduplicate repeated names like "AFRICA, AFRICA, AFRICA"
    const parts = cleanName
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
    return [...new Set(parts)].join(", ");
  }
  if (isUserGeoAllSentinel(code)) {
    if (String(code).includes("COUNTR")) return "All countries";
    if (String(code).includes("ZONE")) return "All zones";
    if (String(code).includes("REGION")) return "All regions";
  }
  // UserMaster 0000 = Excel ALL
  if (!cleanCode && (code === USER_DEFAULT_GEO_CODE || String(code || "").trim() === USER_DEFAULT_GEO_CODE)) {
    return "ALL";
  }
  if (isUserGeoScopedSentinel(code)) return "Multiple selected";
  if (cleanCode) return cleanCode;
  return "-";
}

export function formatUserTerritoryColumnDisplay(rowOrSpec, territoryCode, territoryName) {
  if (!isGeneralUserSpec(rowOrSpec)) return "-";
  const cleanCode = stripDefaultGeoToken(territoryCode);
  const cleanName = stripDefaultGeoToken(territoryName);
  const parts = cleanCode
    ? cleanCode.split(",").map((x) => x.trim()).filter(Boolean)
    : [];
  if (parts.some((p) => isUserTerritoryAllSentinel(p))) {
    return formatUserTerritoryScopeDisplay(USER_TERRITORY_ALL_SENTINEL, null);
  }
  if (cleanName) return cleanName;
  if (isUserGeoScopedSentinel(territoryCode)) return "Multiple selected";
  if (cleanCode) return cleanCode;
  return "-";
}

export function formatUserTerritoryScopeDisplay(territoryCode, territoryName) {
  if (isUserTerritoryAllSentinel(territoryCode)) {
    return "All territories (within selected regions)";
  }
  const n = territoryName && String(territoryName).trim();
  if (n) return n;
  const c = territoryCode && String(territoryCode).trim();
  return c || "-";
}

/** User type that sees all countries on /csrp/dashboard geo filters. */
export const SE_DASHBOARD_ALL_COUNTRIES_USER_TYPE = "UT0000012";

export function getLoggedInUserFromStorage() {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Map user market code/name to M001 (Domestic) or M002 (Export). */
export function normalizeUserMarketCode(rawCode, rawName) {
  const code = String(rawCode ?? "").trim().toUpperCase();
  const name = String(rawName ?? "").trim().toLowerCase();
  if (code === "M001" || code === "DOMESTIC" || name === "domestic") return "M001";
  if (code === "M002" || code === "EXPORT" || name === "export") return "M002";
  if (code.includes("DOM") && !code.includes("EXP")) return "M001";
  if (code.includes("EXP")) return "M002";
  return null;
}

/**
 * When the logged-in user is assigned exactly one market (M001 or M002),
 * /csrp/dashboard Market filter is locked to that value. Returns null if not locked.
 */
export function getUserDashboardLockedMarket(user) {
  if (!user) return null;

  const fromArr = user.marketCodes ?? user.MarketCodes;
  let rawCodes = [];
  if (Array.isArray(fromArr) && fromArr.length) {
    rawCodes = fromArr.map((c) => String(c).trim()).filter(Boolean);
  } else {
    const csv = String(user.marketCode ?? user.MarketCode ?? "").trim();
    if (csv) {
      rawCodes = csv.split(",").map((c) => c.trim()).filter(Boolean);
    }
  }

  const marketName = user.marketName ?? user.MarketName ?? "";
  const normalized = rawCodes.length
    ? rawCodes.map((c) => normalizeUserMarketCode(c, marketName)).filter(Boolean)
    : [normalizeUserMarketCode(user.marketCode ?? user.MarketCode, marketName)].filter(Boolean);

  const unique = [...new Set(normalized)];
  if (unique.length === 1) return unique[0];
  return null;
}

/** Normalized M001/M002 list from login user (may be empty if not assigned). */
export function getUserAssignedMarketCodes(user) {
  if (!user) return [];

  const fromArr = user.marketCodes ?? user.MarketCodes;
  let rawCodes = [];
  if (Array.isArray(fromArr) && fromArr.length) {
    rawCodes = fromArr.map((c) => String(c).trim()).filter(Boolean);
  } else {
    const csv = String(user.marketCode ?? user.MarketCode ?? "").trim();
    if (csv) {
      rawCodes = csv.split(",").map((c) => c.trim()).filter(Boolean);
    }
  }

  const marketName = user.marketName ?? user.MarketName ?? "";
  const normalized = rawCodes.length
    ? rawCodes.map((c) => normalizeUserMarketCode(c, marketName)).filter(Boolean)
    : [normalizeUserMarketCode(user.marketCode ?? user.MarketCode, marketName)].filter(Boolean);

  return [...new Set(normalized)];
}

/** Restrict market dropdown options to the logged-in user's assigned market(s). */
export function filterMarketsForDashboardUser(markets, user) {
  const list = Array.isArray(markets) ? markets : [];
  const assigned = getUserAssignedMarketCodes(user);
  if (!assigned.length) return list;

  const allowed = new Set(assigned.map((c) => String(c).trim().toUpperCase()));
  return list.filter((m) => {
    const id = String(m?.id ?? m?.marketCode ?? m?.MarketCode ?? "").trim().toUpperCase();
    return id && allowed.has(id);
  });
}

/** True when user is assigned ALL at this geo level (0000 / scope ALL / UT0000012 for country). */
export function isUserGeoAllScopeForDashboard(user, level) {
  if (!user) return false;
  if (level === "country" && isSeDashboardAllCountriesUser(user)) return true;
  const picked = pickUserGeoCodesFromApi(user, level);
  if (picked.expandAll || String(picked.scopeMode || "").toUpperCase() === "ALL") {
    return true;
  }
  const nameKeys = {
    country: ["countryName", "CountryName"],
    zone: ["zoneName", "ZoneName"],
    region: ["regionName", "RegionName"],
    territory: ["territoryName", "TerritoryName"],
  };
  for (const key of nameKeys[level] || []) {
    if (String(user?.[key] ?? "").trim().toUpperCase() === "ALL") return true;
  }
  return false;
}

export function isGeoAllSelectionToken(id) {
  const s = String(id ?? "").trim().toLowerCase();
  return s === "all" || s === USER_DEFAULT_GEO_CODE;
}

/** Map { id } options to code list, omitting All / 0000 rows. */
export function geoOptionIdsFromList(options) {
  return (Array.isArray(options) ? options : [])
    .map((o) => String(o?.id ?? "").trim())
    .filter((id) => id && !isGeoAllSelectionToken(id));
}

/**
 * When selection includes All / 0000, expand to every option in the current dropdown
 * (already scoped by parent cascade + user assignment).
 */
export function resolveGeoFilterSelection(selected, options) {
  const sel = Array.isArray(selected)
    ? selected.map((x) => String(x ?? "").trim()).filter(Boolean)
    : [];
  if (!sel.some(isGeoAllSelectionToken)) {
    return sel.filter((id) => !isGeoAllSelectionToken(id));
  }
  return geoOptionIdsFromList(options);
}

export function isSeDashboardAllCountriesUser(user) {
  const code = user?.userTypeCode ?? user?.UserTypeCode ?? "";
  return String(code).trim() === SE_DASHBOARD_ALL_COUNTRIES_USER_TYPE;
}

/** null = no restriction at this level; [] = none assigned; undefined = not in geography payload. */
export function getUserScopedGeoCodesFromGeography(geography, level) {
  if (!geography) return undefined;
  const levelKeys = {
    country: "COUNTRY",
    zone: "ZONE",
    region: "REGION",
    territory: "TERRITORY",
  };
  const want = levelKeys[level];
  if (!want) return undefined;
  const levels = geography?.levels ?? geography?.Levels ?? [];
  const row = levels.find(
    (l) => String(l.geoLevel ?? l.GeoLevel ?? "").toUpperCase() === want
  );
  if (!row) return undefined;
  const mode = String(row.scopeMode ?? row.ScopeMode ?? "").toUpperCase();
  const csvVal = String(
    row.userMasterColumnValue ?? row.UserMasterColumnValue ?? ""
  ).trim();
  const csvTokens = splitCsv(csvVal);
  const codes = (row.codes ?? row.Codes ?? [])
    .map((c) => String(c.geoCode ?? c.GeoCode ?? "").trim())
    .filter((c) => c && !isGeoAllSelectionToken(c));
  if (codes.length) return codes;
  if (mode === "ALL") return null;
  if (mode === "NONE") {
    // UserLocationMapping rows with only 0000 at this level: API may report NONE + csv 0000.
    const ulmAllAtLevel =
      csvVal === USER_DEFAULT_GEO_CODE ||
      (csvTokens.length > 0 && csvTokens.every((t) => isGeoAllSelectionToken(t)));
    if (ulmAllAtLevel) return null;
    return [];
  }
  return undefined;
}

/** null = no restriction at this level; [] = none assigned. */
export function getUserScopedGeoCodesForDashboard(user, level, geography = null) {
  if (!user) return null;
  if (level === "country" && isSeDashboardAllCountriesUser(user)) return null;

  const fromGeography = getUserScopedGeoCodesFromGeography(geography, level);
  if (fromGeography === null) return null;
  if (fromGeography !== undefined && fromGeography.length > 0) return fromGeography;

  const fromMapping = applyLocationMappingsToForm(user);
  if (fromMapping) {
    const key = `${level}Codes`;
    const mapped = (fromMapping[key] || []).filter((c) => c && !isGeoAllSelectionToken(c));
    if (mapped.length) return mapped;
  }

  const picked = pickUserGeoCodesFromApi(user, level);
  if (picked.expandAll || String(picked.scopeMode).toUpperCase() === "ALL") {
    return null;
  }
  if (fromGeography !== undefined) return fromGeography;
  return picked.codes;
}

/** Filter { id, text } geo dropdown options to the logged-in user's assignment. */
export function filterGeoOptionsForDashboardUser(options, user, level, geography = null) {
  const list = Array.isArray(options) ? options : [];
  const allowed = getUserScopedGeoCodesForDashboard(user, level, geography);
  if (allowed === null) return list;
  if (!allowed.length) return [];

  const allowedSet = new Set(allowed.map((c) => String(c).trim().toUpperCase()));
  return list.filter((o) => {
    const id = String(o?.id ?? "").trim();
    if (id && allowedSet.has(id.toUpperCase())) return true;
    // Export grouped zones: option.id is one canonical code; match any linked zoneCodes.
    if (Array.isArray(o?.zoneCodes) && o.zoneCodes.length) {
      return o.zoneCodes.some((zc) =>
        allowedSet.has(String(zc ?? "").trim().toUpperCase())
      );
    }
    return false;
  });
}

/** @deprecated use getUserScopedGeoCodesForDashboard(user, "country") */
export function getUserScopedCountryCodesForDashboard(user) {
  return getUserScopedGeoCodesForDashboard(user, "country");
}

const isDashboardGeoOmittedOption = (code, name) => {
  const c = String(code ?? "").trim().toLowerCase();
  const n = String(name ?? "").trim().toLowerCase();
  return !c || c === "all" || c === USER_DEFAULT_GEO_CODE || n === "all";
};

/** Build Country dropdown options for SE dashboard from master list + logged-in user scope. */
export function filterMasterCountriesForDashboardUser(allCountries, user, geography = null) {
  const list = Array.isArray(allCountries) ? allCountries : [];
  const normalized = list
    .map((country) => {
      const id = String(country.countryCode ?? country.CountryCode ?? "").trim();
      const text = String(country.countryName ?? country.CountryName ?? "").trim();
      return { id, text: text || id };
    })
    .filter((c) => c.id && !isDashboardGeoOmittedOption(c.id, c.text));

  if (!user || isSeDashboardAllCountriesUser(user)) {
    return normalized;
  }

  return filterGeoOptionsForDashboardUser(normalized, user, "country", geography);
}
